import { ApiError, type Api } from "../api/client.js";
import type { AimHandlers } from "../ui/aim.js";
import { LimitsPage } from "../ui/limits.js";
import type { Tab } from "../ui/tabs.js";
import { limitsModel, type LimitsModel } from "./limits-model.js";

/**
 * The Limits page's controller (DCTA-154): one read, `GET /v1/limits`, drawn as `limitsModel` says, every time the page
 * opens (today's counters and waits move). Read only — no action is served here. A session that ended goes to the door;
 * any other failure is the page's "couldn't load", with no figure on it.
 */
export interface LimitsDeps {
  readonly api: Api;
  /** The bank's clock (lib/bank-clock.ts): a new payee's wait is still running against it. */
  readonly now: () => number;
  readonly onBack: () => void;
  readonly onSignedOut: () => void;
  readonly onTab: (tab: Tab) => void;
  readonly assistant: AimHandlers;
}

export class LimitsScreen {
  readonly view: LimitsPage;
  readonly #d: LimitsDeps;
  #model: LimitsModel | undefined;

  constructor(deps: LimitsDeps) {
    this.#d = deps;
    this.view = new LimitsPage({ onBack: deps.onBack, onRetry: () => void this.refresh(), onTab: deps.onTab, assistant: deps.assistant });
  }

  async refresh(): Promise<void> {
    if (this.#model === undefined) this.view.loading();
    try {
      this.#model = limitsModel(await this.#d.api.limits(), this.#d.now());
      this.view.show(this.#model);
    } catch (error) {
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
        this.#d.onSignedOut();
        return;
      }
      this.#model = undefined;
      this.view.unreachable();
    }
  }
}
